import { ReactElement, useCallback } from 'react';
import { CatInlineNotification } from '@blocks-react/components/InlineNotification';
import { CatButton } from '@blocks-react/components/Button';
import { CatIconSuccess } from '@blocks-react/components/Icons/Success';
import { CatIconWarningTriangle } from '@blocks-react/components/Icons/WarningTriangle';
import { CatIconWarningCircle } from '@blocks-react/components/Icons/WarningCircle';
import { CatIconInfo } from '@blocks-react/components/Icons/Info';
import cx from 'classnames';

export type variantType = 'success' | 'warning' | 'error' | 'info';
export type buttonType = 'button' | 'submit' | 'reset';
export type buttonVariantype = 'primary' | 'contrast' | 'ghost' | 'danger';
export type buttonsAlignmentType = 'row' | 'column';
export type buttonPropType = {
  buttonText?: string;
  className?: string;
  slot?: string;
  type?: buttonType;
  variant?: buttonVariantype;
  onClick?: () => void;
  href?: string;
};

type ErrorBannerProps = {
  heading: string;
  children?: ReactElement | ReactElement[];
  variant?: variantType;
  buttonProp?: buttonPropType | null;
  secondButtonProp?: buttonPropType | null;
  dismissible?: boolean;
  isDismissed?: boolean;
  addIcon?: boolean;
  buttonsAlignment?: buttonsAlignmentType;
  buttonsSlot?: string;
  onClose?: () => void;
};

export const ErrorBanner = ({
  heading,
  children,
  variant = 'error',
  buttonsSlot = '',
  buttonProp = null,
  secondButtonProp = null,
  dismissible = false,
  isDismissed = false,
  addIcon = false,
  buttonsAlignment = 'row',
  onClose
}: ErrorBannerProps) => {
  const { buttonText, ...restProps } = buttonProp ?? {};
  const { buttonText: secondButtonText, ...secondRestProps } =
    secondButtonProp ?? {};

  const iconPerVariant = {
    success: <CatIconSuccess slot="before" />,
    warning: <CatIconWarningTriangle slot="before" />,
    error: <CatIconWarningCircle slot="before" />,
    info: <CatIconInfo slot="before" />
  };

  const hasAfterSlot = buttonsSlot === 'after' || buttonProp?.slot === 'after';

  const buttons =
    buttonProp || secondButtonProp ? (
      <div
        className={cx({
          'd-flex': buttonsAlignment === 'row',
          'pt-3': !hasAfterSlot
        })}
        {...(hasAfterSlot && { slot: 'after' })}
      >
        {buttonProp ? <CatButton {...restProps}>{buttonText}</CatButton> : null}
        {secondButtonProp ? (
          <CatButton {...secondRestProps}>{secondButtonText}</CatButton>
        ) : null}
      </div>
    ) : null;

  const handleNotificationClose = useCallback(() => {
    if (onClose) {
      onClose();
    }
  }, [onClose]);

  return (
    <CatInlineNotification
      heading={heading}
      variant={variant === 'info' ? undefined : variant}
      dismissible={dismissible}
      isDismissed={isDismissed}
      onBlInlineNotificationClose={handleNotificationClose}
      data-testid={'cat-inline-notification'}
    >
      {addIcon && iconPerVariant[variant]}
      {children}
      {buttons}
    </CatInlineNotification>
  );
};
